import React from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import Button from '../UI/Button';
import { StepProps } from '../../types';

interface Step3Data {
  license_number: string;
  years_in_practice: number;
  professional_body: string;
  membership_number: string;
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h2<{ theme: any }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const Description = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const Select = styled.select.withConfig({
  shouldForwardProp: (prop) => !['theme', 'hasError'].includes(prop),
})<{ theme: any; hasError: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.hasError ? props.theme.colors.error : props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Raleway', sans-serif;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? props.theme.colors.error : props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.hasError ? props.theme.colors.error : props.theme.colors.primary}33;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label<{ theme: any }>`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
`;

const ErrorMessage = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.error};
  font-size: 0.75rem;
  font-weight: 500;
`;


const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const professionalBodies = [
  { label: 'Institute of Chartered Accountants of Nigeria (ICAN)', value: 'ICAN' },
  { label: 'Association of National Accountants of Nigeria (ANAN)', value: 'ANAN' },
  { label: 'Chartered Institute of Taxation of Nigeria (CITN)', value: 'CITN' },
  { label: 'Nigerian Bar Association (NBA)', value: 'NBA' },
];

const Step3ProfessionalInfo: React.FC<StepProps> = ({ data, onNext, onPrevious }) => {
  const { theme } = useTheme();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Step3Data>({
    defaultValues: {
      license_number: data.license_number || '',
      years_in_practice: data.years_in_practice || 0,
      professional_body: data.professional_body || '',
      membership_number: data.membership_number || '',
    },
  });

  const onSubmit = (formData: Step3Data) => {
    onNext(formData);
  };


  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <HeaderContainer>
        <Title theme={theme}>Professional Information</Title>
        <Description theme={theme}>
          Please provide your professional credentials and practice details.
        </Description>
      </HeaderContainer>

      <InputGroup>
        <Label theme={theme}>
          Professional License Number <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <input
          type="text"
          placeholder="Enter your professional license number"
          style={{
            padding: '0.75rem',
            border: `2px solid ${errors.license_number ? theme.colors.error : theme.colors.border}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontFamily: 'Raleway, sans-serif',
            backgroundColor: theme.colors.background,
            color: theme.colors.text,
            transition: 'all 0.3s ease',
          }}
          {...register('license_number', {
            required: 'License number is required',
            minLength: {
              value: 3,
              message: 'License number must be at least 3 characters',
            },
          })}
        />
        {errors.license_number && (
          <ErrorMessage theme={theme}>{errors.license_number.message}</ErrorMessage>
        )}
      </InputGroup>

      <InputGroup>
        <Label theme={theme}>
          Years in Practice <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <input
          type="number"
          placeholder="Enter number of years in practice"
          style={{
            padding: '0.75rem',
            border: `2px solid ${errors.years_in_practice ? theme.colors.error : theme.colors.border}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontFamily: 'Raleway, sans-serif',
            backgroundColor: theme.colors.background,
            color: theme.colors.text,
            transition: 'all 0.3s ease',
          }}
          {...register('years_in_practice', {
            required: 'Years in practice is required',
            valueAsNumber: true,
            min: {
              value: 0,
              message: 'Years in practice cannot be negative',
            },
            max: {
              value: 50,
              message: 'Please enter a realistic number of years',
            },
          })}
        />
        {errors.years_in_practice && (
          <ErrorMessage theme={theme}>{errors.years_in_practice.message}</ErrorMessage>
        )}
      </InputGroup>

      <SelectGroup>
        <Label theme={theme}>
          Professional Body <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <Select
          theme={theme}
          hasError={!!errors.professional_body}
          {...register('professional_body', {
            required: 'Professional body is required',
          })}
        >
          <option value="">Select your professional body</option>
          {professionalBodies.map((body) => (
            <option key={body.value} value={body.value}>
              {body.label}
            </option>
          ))}
        </Select>
        {errors.professional_body && (
          <ErrorMessage theme={theme}>{errors.professional_body.message}</ErrorMessage>
        )}
      </SelectGroup>

      <InputGroup>
        <Label theme={theme}>
          Membership Number <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <input
          type="text"
          placeholder="Enter your membership number"
          style={{
            padding: '0.75rem',
            border: `2px solid ${errors.membership_number ? theme.colors.error : theme.colors.border}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontFamily: 'Raleway, sans-serif',
            backgroundColor: theme.colors.background,
            color: theme.colors.text,
            transition: 'all 0.3s ease',
          }}
          {...register('membership_number', {
            required: 'Membership number is required',
          })}
        />
        {errors.membership_number && (
          <ErrorMessage theme={theme}>{errors.membership_number.message}</ErrorMessage>
        )}
      </InputGroup>

      <ButtonContainer>
        <Button type="button" variant="outline" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="submit" size="large">
          Next Step
        </Button>
      </ButtonContainer>
    </Form>
  );
};

export default Step3ProfessionalInfo;
